// LN Pro · THE ACTIONS DESK — the surface PKT-0633's contract was missing.
//
// That packet landed a source-agnostic ActionCard, adapters for Reddit (#1) and
// Instagram (#2), and its own tests — and NOTHING in the app rendered one. A
// contract with no consumer is a types file, so this is the consumer: one desk
// that draws an ActionCard whatever produced it.
//
// It does NOT replace RedditAnswerDesk. PKT-0633's stop_if is explicit that a
// working surface is not spent to buy a cleaner abstraction, so Reddit keeps
// the desk it has and this one carries adapter #2 onward.
//
// THREE THINGS THIS FILE REFUSES TO DO, each of them the packet's own
// proof_required rather than my preference:
//
//   It does not render an `unknown` edge as a blank. An absent chip reads as
//   "nothing to say here", which is how an unverified claim quietly becomes an
//   assumed one. Unknown gets a word.
//
//   It does not let an unknown edge into the score. `socialProofEvidence`
//   counts verified edges only and reports the unknowns beside the number, so
//   a reader cannot receive a score without also receiving what is missing
//   from it.
//
//   It cannot post. There is no executor and none is reachable: the verbs come
//   from `verbsFor`, every one of them records what a HUMAN did, and the card's
//   `posting_enabled` is the literal type `false` — an executor here is
//   unrepresentable, not merely absent.

import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  edgeLabel,
  socialProofEvidence,
  type ActionCard,
  type ActionQueueState,
  type ActionVerb,
  type EdgeEvidence,
} from "../../services/actionsContract";
import { verbsFor } from "../../services/actionAdapters";

const FILTERS: Array<[ActionQueueState, string]> = [
  ["needs_action", "Needs action"],
  ["done", "Done"],
  ["dismissed", "Dismissed"],
  ["all", "All"],
];

export interface ActionDeskProps {
  cards: ActionCard[];
  counts: Partial<Record<ActionQueueState, number>>;
  filter: ActionQueueState;
  /** Only expose states the current producer actually serves. Omitted keeps all states. */
  filterOptions?: readonly ActionQueueState[];
  onFilter: (state: ActionQueueState) => void;
  /** Records what the operator did. Never posts anything anywhere. */
  onAct: (card: ActionCard, verb: ActionVerb, text: string | null, dismissReason?: string) => Promise<void>;
  /** Unconfirmed/recorded receipts must not be submitted again from this view. */
  blockedActionIds?: ReadonlySet<string>;
  loading?: boolean;
  /** The read failed — say how, rather than drawing an empty desk. */
  error?: string | null;
  /** Why the desk is empty when it legitimately is. */
  emptyNote?: string;
}

function EdgeChip({ edge }: { edge: EdgeEvidence }) {
  const theme = useTheme();
  const colour =
    edge.state === "verified"
      ? theme.palette.success.main
      : edge.state === "refuted"
        ? theme.palette.error.main
        : theme.palette.warning.main;
  return (
    <Tooltip arrow title={edge.because}>
      <Chip
        size="small"
        variant="outlined"
        data-testid={`edge-${edge.state}-${edge.subject}`}
        label={edgeLabel(edge)}
        sx={{ height: 22, fontSize: 11.5, color: colour, borderColor: alpha(colour, 0.5) }}
      />
    </Tooltip>
  );
}

export default function ActionDesk({
  cards,
  counts,
  filter,
  filterOptions,
  onFilter,
  onAct,
  loading,
  error,
  emptyNote,
  blockedActionIds,
}: ActionDeskProps) {
  const theme = useTheme();
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});
  const [dismissReasons, setDismissReasons] = React.useState<Record<string, string>>({});
  const busyRef = React.useRef(false);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [actError, setActError] = React.useState<string | null>(null);

  const act = async (card: ActionCard, verb: ActionVerb) => {
    if (busyRef.current || blockedActionIds?.has(card.id)) return;
    const reason = dismissReasons[card.id]?.trim();
    if (card.source === "instagram" && verb === "dismissed" && !reason) return;
    busyRef.current = true;
    setBusy(card.id);
    setActError(null);
    try {
      const text = card.suggested_text === null ? null : drafts[card.id] ?? card.suggested_text;
      if (card.source === "instagram" && verb === "dismissed") await onAct(card, verb, text, reason);
      else await onAct(card, verb, text);
    } catch (err) {
      setActError(err instanceof Error ? err.message : "The action was not recorded. Try again.");
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  if (error) {
    return (
      <Alert severity="error" data-testid="action-desk-error">
        This desk could not be read: {error}
      </Alert>
    );
  }
  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress aria-label="Loading actions" />
      </Box>
    );
  }

  return (
    <Stack spacing={2} data-testid="action-desk">
      {actError && <Alert severity="error" data-testid="action-desk-act-error">{actError}</Alert>}

      <Stack direction="row" gap={1} flexWrap="wrap">
        {FILTERS.filter(([id]) => !filterOptions || filterOptions.includes(id)).map(([id, label]) => (
          <Chip
            key={id}
            data-testid={`action-filter-${id}`}
            label={`${label} ${counts[id] ?? 0}`}
            color={filter === id ? "primary" : "default"}
            onClick={() => onFilter(id)}
          />
        ))}
      </Stack>

      {cards.length === 0 && (
        <Typography variant="body2" color="text.secondary" data-testid="action-desk-empty">
          {emptyNote ?? "Nothing in this queue."}
        </Typography>
      )}

      {cards.map((card) => {
        const proof = socialProofEvidence(card);
        const hasText = card.suggested_text !== null;
        const draft = drafts[card.id] ?? card.suggested_text ?? "";
        return (
          <Box
            key={card.id}
            data-testid={`action-card-${card.id}`}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}
          >
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
              <Chip size="small" label={card.source} data-testid={`action-source-${card.id}`} />
              <Chip
                size="small"
                color={card.verdict === "HOT" ? "error" : "warning"}
                label={`${card.verdict} ${card.score}`}
              />
            </Stack>

            <Typography variant="h6" sx={{ mt: 1 }}>{card.title}</Typography>
            {card.body_preview && <Typography>{card.body_preview}</Typography>}
            {card.why && (
              <Typography color="text.secondary" variant="body2">why: {card.why}</Typography>
            )}

            {/* Evidence, with its verification state on its face. */}
            {card.evidence.length > 0 && (
              <Stack direction="row" gap={0.75} flexWrap="wrap" sx={{ mt: 1 }} data-testid={`action-edges-${card.id}`}>
                {card.evidence.map((edge) => (
                  <EdgeChip key={`${edge.kind}:${edge.subject}`} edge={edge} />
                ))}
              </Stack>
            )}
            {card.evidence.length > 0 && (
              <Typography
                variant="caption"
                data-testid={`action-proof-${card.id}`}
                sx={{ display: "block", mt: 0.5, color: proof.hasUnverifiedClaim ? "warning.main" : "text.secondary" }}
              >
                {proof.summary}
              </Typography>
            )}

            {/* Text, only where the action HAS text. An empty "Suggested reply"
                box with a Copy button under it is a card that lies about what
                it wants — see instagramOpportunityToCard. */}
            {hasText && (
              <>
                <Alert severity="warning" sx={{ my: 1 }}>
                  DO NOT SEND AS-IS — personalize before copying.
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  label="Suggested reply"
                  value={draft}
                  onChange={(e) => setDrafts({ ...drafts, [card.id]: e.target.value })}
                  inputProps={{ "data-testid": `action-text-${card.id}` } as never}
                />
                {card.disclosure_link && (
                  <Button
                    data-testid={`action-disclose-${card.id}`}
                    onClick={() =>
                      setDrafts({
                        ...drafts,
                        [card.id]: `${draft}\n\nI work with LocateNow Atlanta — this guide may help: ${card.disclosure_link}`,
                      })
                    }
                  >
                    + Insert link with disclosure
                  </Button>
                )}
              </>
            )}

            {card.source === "instagram" && (
              <TextField
                label="Reason for dismissal"
                value={dismissReasons[card.id] ?? ""}
                onChange={(event) => setDismissReasons({ ...dismissReasons, [card.id]: event.target.value })}
                inputProps={{ maxLength: 500, "data-testid": `action-dismiss-reason-${card.id}` }}
                disabled={busy !== null || blockedActionIds?.has(card.id)}
                size="small"
                sx={{ mt: 2 }}
              />
            )}
            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
              {/* The operator goes and acts; we only record it afterwards. */}
              <Button
                href={card.source_url}
                target="_blank"
                rel="noreferrer"
                variant={hasText ? "text" : "contained"}
                data-testid={`action-open-${card.id}`}
              >
                Open {card.source === "reddit" ? "post" : "profile"} ↗
              </Button>
              {verbsFor(card.source).map((verb) => (
                <Button
                  key={verb.primary}
                  disabled={busy !== null || blockedActionIds?.has(card.id) || (card.source === "instagram" && verb.primary === "dismissed" && !dismissReasons[card.id]?.trim())}
                  color={verb.primary === "dismissed" ? "error" : "primary"}
                  variant={verb.primary === "copied" ? "contained" : "text"}
                  data-testid={`action-verb-${verb.primary}-${card.id}`}
                  onClick={() => void act(card, verb.primary as ActionVerb)}
                >
                  {verb.label}
                </Button>
              ))}
            </Stack>
            <Box sx={{ display: "none" }} data-testid={`action-posting-${card.id}`}>
              {String(card.posting_enabled)}
            </Box>
            <Box sx={{ mt: 0.5, color: alpha(theme.palette.text.primary, 0.5) }} />
          </Box>
        );
      })}
    </Stack>
  );
}
